import { MarketingBanner as MarketingBannerType } from '@app/models';

export const MarketingBanner = ({ bannerData }: { bannerData?: MarketingBannerType | null }) => {
    if (!bannerData || !bannerData.config || !bannerData.content) {
        return null;
    }

    const shouldShowCounter = () => {
        if (!bannerData.config.endDate) return false;

        const endDate = new Date(bannerData.config.endDate).getTime();
        const now = new Date().getTime();
        const distance = endDate - now;
        const twentyFourHours = 24 * 60 * 60 * 1000;

        return distance > 0 && distance <= twentyFourHours;
    };

    const showCounter = shouldShowCounter();

    // TODO: remove const and adjust full component once marketing banner is used only on SSR pages
    const scripts = `
        const isBannerClosed = sessionStorage.getItem('marketing-banner-closed') === 'true';
        const banner = document.getElementById('marketing-banner');
        
        if (banner && isBannerClosed) {
            banner.style.display = 'none';
        } else {
                // Delay event listeners to run after React hydration completes
                setTimeout(() => {
                document.querySelector('#marketing-banner-close-btn').addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (banner) {
                        banner.style.display = 'none';
                        sessionStorage.setItem('marketing-banner-closed', 'true');
                    }
                });
                document.querySelector('#marketing-banner').addEventListener('click', () => {
                    window.location.href = '${bannerData.content.ctaUrl}';
                });
            }, 1500);

            const endDate = new Date('${bannerData.config.endDate}').getTime();
            const desktopCounterElement = document.querySelector('.js-banner-desktop .js-counter');
            const mobileCounterElement = document.querySelector('.js-banner-mobile .js-counter');
            const pad = (num) => String(num).padStart(2, '0');

            let countdownInterval;
            
            function updateCounter() {
                const now = new Date().getTime();
                const distance = endDate - now;
                
                if (distance < 0) {
                    if (desktopCounterElement) desktopCounterElement.textContent = '00 : 00 : 00';
                    if (mobileCounterElement) mobileCounterElement.textContent = '00 : 00 : 00';

                    if (banner) {
                        banner.style.display = 'none';
                    }
                    if (countdownInterval) {
                        clearInterval(countdownInterval);
                    }
                    return;
                }
                
                const hours = Math.floor(distance / (1000 * 60 * 60));
                const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((distance % (1000 * 60)) / 1000);

                const timeString = pad(hours) + ' : ' + pad(minutes) + ' : ' + pad(seconds);
                if (desktopCounterElement) desktopCounterElement.textContent = timeString;
                if (mobileCounterElement) mobileCounterElement.textContent = timeString;
            }
            
            if (desktopCounterElement || mobileCounterElement) {
                // Wait for hydration to complete before starting updates
                setTimeout(() => {
                    updateCounter();
                    countdownInterval = setInterval(updateCounter, 1000);
                }, 100);
            }
        }
    `;

    const styles = `
        #marketing-banner {
            font-size: 1rem;
            width: '100%';
            background-color: ${bannerData.content.backgroundColor || '#0050A0'};
            color: ${bannerData.content.textColor || '#FFFFFF'};
            padding: 6px 16px;
            display: grid;
            grid-template-columns: 1fr auto;
            grid-gap: 1rem;
            align-items: center;
            cursor: pointer;
            height: 48px;
        }
        #marketing-banner-close-btn {
            cursor: pointer;
            font-size: 20px;
            background: none;
            border: none;
            outline: none;
            color: ${bannerData.content.textColor || '#FFFFFF'};
            padding: 0.625rem 1rem;
            margin: -0.625rem -1rem;
        }
        .marketing-banner-content {
            display: flex;
            align-items: center;
        }
        .marketing-banner-content--mobile {
            display: none;
        }
        .marketing-banner-link {
            border: 1px solid transparent;
            background: ${bannerData.content.textColor || '#FFFFFF'};
            color: ${bannerData.content.backgroundColor || '#0050A0'};
            padding: 0.25rem 1rem;
            margin-left: 2rem;
            border-radius: 0.25rem;
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
            transition: 0.3s;
            
            &:hover {
                border-color: ${bannerData.content.textColor || '#FFFFFF'};
                background: ${bannerData.content.backgroundColor || '#0050A0'};
                color: ${bannerData.content.textColor || '#FFFFFF'};
            }
        }
        .marketing-banner-content--desktop .marketing-banner-link__text::after {
            content: '\\203A';
            line-height: 0.5;
            font-size: 2rem;
            margin-left: 0.5rem;
            vertical-align: text-top;
        }
        .marketing-banner-counter {
            font-weight: bold;
            font-size: 1rem;
            margin-left: 1rem;
            display: inline-flex;
            gap: 0.25rem;
            width: 86px;
            line-height: 1.2;
        }
        .counter-value {
            font-weight: bold;
        }

        @media (max-width: 767px) {
            #marketing-banner {
                font-size: 0.875rem;
            }
            .marketing-banner-content--desktop {
                display: none;
            }
            .marketing-banner-content--mobile {
                display: flex;
                width: 100%;
            }
            .marketing-banner-counter {
                margin-left: 0.5rem;
            }
            .marketing-banner-link {
                margin-left: 0.5rem;
            }
            .marketing-banner-link--chevron {
                background: none;
                color: ${bannerData.content.textColor || '#FFFFFF'};
                margin-left: 0.675rem;
                padding: 0;
                height: 1.125rem;
            }
            .marketing-banner-link--chevron:after {
                content: '\\203A';
                line-height: 0.5;
                font-size: 2rem;
                display: block;
                margin-top: -4px;
            }
        }
    `;

    const counterElement = (
        <span className="marketing-banner-counter js-counter" suppressHydrationWarning>
            00 : 00 : 00
        </span>
    );

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: styles }} />

            <div id="marketing-banner">
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <div className="marketing-banner-content marketing-banner-content--desktop js-banner-desktop">
                        {bannerData.content.desktopText}
                        {showCounter && counterElement}
                        <button className="marketing-banner-link">
                            <span className="marketing-banner-link__text">
                                {bannerData.content.ctaText}
                            </span>
                        </button>
                    </div>
                    <div className="marketing-banner-content marketing-banner-content--mobile js-banner-mobile">
                        {bannerData.content.mobileText}
                        {showCounter && counterElement}
                        <button
                            className={
                                bannerData.content.ctaTextMobile
                                    ? 'marketing-banner-link'
                                    : 'marketing-banner-link--chevron'
                            }
                        >
                            {bannerData.content.ctaTextMobile ? (
                                <span className="marketing-banner-link__text">
                                    {bannerData.content.ctaTextMobile}
                                </span>
                            ) : null}
                        </button>
                    </div>
                </div>
                <button id="marketing-banner-close-btn" type="button">
                    ✕
                </button>
            </div>

            <script dangerouslySetInnerHTML={{ __html: scripts }} />
        </>
    );
};
